import api from '@/api'
import i18n from '@/i18n'
import moment from 'moment'
import {
  getDatePattern,
  getDatetimePattern,
  areDatesConsecutive,
} from '@/utils/dates.js'
import store from '@/store'
import { APIV3RequestDatetimeFormat } from '@/config/simpliciti-apis.js'
import { csvToJson } from '@/utils/csv'
import { getTranslationsFromArrayOfString } from '@/utils/translation'
import { exportToCSV } from '@/services/export-service'

const momentDatetimeToFileNameDate = (date) => date.format('DDMMYYYY[_]HHmm')
const momentDateToFileNameDate = (date) =>
  date.format(getDatePattern()).split('/').join('')

let showToastDismissTimeout = null

function showToast(text, timeout = false) {
  store.dispatch('alert/removeAlert')
  store.dispatch('alert/addAlert', {
    type: text.includes('fail') ? 'warning' : 'info',
    title: 'export_feature.toast_title',
    text,
  })
  if (timeout) {
    window.clearTimeout(showToastDismissTimeout)
    showToastDismissTimeout = setTimeout(() => {
      store.dispatch('alert/removeAlert')
    }, 2000)
  }
}

export const actions = {
  async exportCreateCircuit(
    { rootGetters },
    { roundName, transportTypeId, roundCategoryId, activityId }
  ) {
    try {
      showToast('export_feature_create_circuit_progress_toast')

      let selectedItem = rootGetters['location_module/selectedItem']
      let vehicleId = selectedItem.vehicleId
      let url =
        api.APIUrls.APIV3_GEORED_SERVICE_HISTORY_TO_ROUND.split(
          '${vehicleId}'
        ).join(vehicleId)
      let startDatetime, endDatetime, transportType

      transportType = transportTypeId
      startDatetime = moment(selectedItem.historyStartDatetimeStamp).format(
        APIV3RequestDatetimeFormat
      )
      endDatetime = moment(selectedItem.historyEndDatetimeStamp).format(
        APIV3RequestDatetimeFormat
      )

      let params = {
        startDatetime,
        endDatetime,
        roundName,
        roundCategoryId,
        transportType,
        activityId,
      }

      let filename = `circuit_${roundName}_${startDatetime
        .split(' ')
        .join('_')}__to__${endDatetime.split(' ').join('_')}.xml`
      await api.v3.get(url, params, {
        isDownload: true,
        defaultDownloadFilename: filename,
        forceDefaultDownloaFilename: false,
        headers: {
          accept: 'application/xml',
        },
        responseType: 'blob',
      })
      showToast('export_feature.file_export_success', true)
    } catch (err) {
      showToast('export_feature.file_export_fail', true)
      console.error('Fail to export pendulum', {
        err,
      })
    }
  },
  async exportHistoryCircuitExecutionPendulum({ rootGetters }) {
    try {
      showToast('export_feature.file_export_in_progress')
      let type = rootGetters['search_module/activeSearchFormTabName'] //circuit
      let selectedItem = rootGetters['location_module/selectedItem']
      let circuitName = selectedItem.circuitName
      let circuitExecutionId = selectedItem.circuitExecutionId
      let url = api.APIUrls.APIV3_GEORED_SERVICE_ROUND_EXECUTION_PENDULUM.split(
        '${roundExecutionId}'
      ).join(circuitExecutionId)

      let datePart = ''
      if (selectedItem.circuitDatetimeFrom && selectedItem.circuitDatetimeTo) {
        datePart = `_${momentDatetimeToFileNameDate(
          moment(selectedItem.circuitDatetimeFrom)
        )}-to-${momentDatetimeToFileNameDate(
          moment(selectedItem.circuitDatetimeTo)
        )}`
      }
      await api.v3.get(
        `${url}`,
        {},
        {
          isDownload: true,
          defaultDownloadFilename: `history_${type}_${circuitName}${datePart}.xml`,
          forceDefaultDownloaFilename: false,
          headers: {
            accept: 'application/xml',
          },
          responseType: 'blob',
        }
      )
      showToast('export_feature.file_export_success', true)
    } catch (err) {
      showToast('export_feature.file_export_fail', true)
      console.error('Fail to export pendulum', {
        err,
      })
    }
  },
  async exportHistoryVehicleShape({ rootGetters }) {
    try {
      showToast('export_feature.file_export_in_progress')
      let type = rootGetters['search_module/activeSearchFormTabName'] //vehicle
      let selectedItem = rootGetters['location_module/selectedItem']
      let vehicleId = selectedItem.vehicleId
      let datePart = moment(selectedItem.timestamp)
        .format(getDatePattern())
        .split('/')
        .join('')
      let startDatetime = moment(selectedItem.historyStartDatetimeStamp).format(
        APIV3RequestDatetimeFormat
      )
      let endDatetime = moment(selectedItem.historyEndDatetimeStamp).format(
        APIV3RequestDatetimeFormat
      )

      await api.v3.get(
        `${api.APIUrls.APIV3_GEORED_SERVICE_HISTORY_TO_SHAPE}/${vehicleId}`,
        {
          startDatetime,
          endDatetime,
        },
        {
          isDownload: true,
          defaultDownloadFilename: `history_${type}_${datePart}.zip`,
          forceDefaultDownloaFilename: false,
          headers: {
            accept: 'application/zip',
          },
          responseType: 'blob',
        }
      )
      showToast('export_feature.file_export_success', true)
    } catch (err) {
      showToast('export_feature.file_export_fail', true)
      console.error('Fail to export shape', {
        err,
      })
    }
  },
  /**
   * Used by Location - History (Vehicle/Driver) - Export all selected vehicles
   * Used by Location - Realtime (Vehicle/Driver) - Last history tab - Export selected vehicle
   * 090523 JAR Improve name computation
   * @param {*} param0
   */
  async exportHistoryKML({ rootGetters, rootState }, isSingleVehicle = false) {
    try {
      showToast('export_feature.file_export_in_progress')
      let type = rootGetters['search_module/activeSearchFormTabName'] //vehicle/driver

      let selection = rootGetters['search_module/getSelection']
      let selectedDateRanges = selection.selectedDateRanges

      //History filename contains the date of the search

      const areDatesConsecutiveFlag =
        selectedDateRanges.length > 1 &&
        areDatesConsecutive(selectedDateRanges.map((datesArr) => datesArr[0]))

      let datePart = ''

      if (!isSingleVehicle) {
        //If dates are non-consecutive, generate something like 01012023-05012023-10012023
        if (!areDatesConsecutiveFlag) {
          let formattedDates = selectedDateRanges.map((datesArr) => {
            return momentDateToFileNameDate(moment(datesArr[0]))
          })
          formattedDates = formattedDates.filter((str, index) => {
            return formattedDates.indexOf(str) === index
          })
          datePart = formattedDates.join('-and-')
        } else {
          if (selectedDateRanges.length > 1) {
            //Else, just join start-end dates like 01012023-10012023
            datePart = `${momentDateToFileNameDate(
              moment(selectedDateRanges[0][0])
            )}-to-${momentDateToFileNameDate(
              moment(selectedDateRanges[selectedDateRanges.length - 1][0])
            )}`
          } else {
            datePart = momentDateToFileNameDate(
              momnet(selectedDateRanges[0][0])
            )
          }
        }
      }

      let applyHourOnlyOnFirstAndLastDate =
        rootState.search_module.isFirstAndLastDay

      let ids, dates, startTime, endTime

      if (!isSingleVehicle) {
        ids =
          selection[
            type === 'vehicle' ? 'selectedVehiclesIds' : 'selectedDriversIds'
          ]

        dates = selectedDateRanges.map((range) => {
          return range[0]
        })
        startTime = moment(
          selectedDateRanges[selectedDateRanges.length - 1][0]
        ).format('HH:mm')
        endTime = moment(
          selectedDateRanges[selectedDateRanges.length - 1][1]
        ).format('HH:mm')
        dates = dates.map((d) => moment(d).format(`YYYY-MM-DD`))
        dates = dates.filter((d, i) => dates.indexOf(d) === i)
      }

      if (isSingleVehicle) {
        let selectedItem = rootGetters['location_module/selectedItem']
        ids = [selectedItem.vehicleId]

        dates = [
          moment(selectedItem.historyStartDatetimeStamp).format('YYYY-MM-DD'),
        ]
        startTime = moment(selectedItem.historyStartDatetimeStamp).format(
          'HH:mm'
        )
        endTime = moment(selectedItem.historyEndDatetimeStamp).format('HH:mm')
      }

      if (type === 'circuit') {
        type = 'vehicle'
      }

      await api.v3.get(
        `${api.APIUrls.APIV3_GEORED_SERVICE_HISTORY_TO_KML}/${type}`,
        {
          id: ids,
          dates,
          startTime,
          endTime,
          applyHourOnlyOnFirstAndLastDate,
        },
        {
          isDownload: true,
          defaultDownloadFilename: `history_${type}_${datePart}.kml`,
          forceDefaultDownloaFilename: false,
          headers: {
            accept: 'application/xml',
          },
          responseType: 'blob',
        }
      )
      showToast('export_feature.file_export_success', true)
    } catch (err) {
      showToast('export_feature.file_export_fail', true)
      console.error('Fail to export kml', {
        err,
      })
    }
  },
  /**
   * Only supports realtime by vehicle
   * @param {*} param0
   */
  async exportRealtimeKML({ rootGetters }) {
    try {
      showToast('export_feature.file_export_in_progress')
      let locale = i18n.locale
      let datePart = moment().format(getDatePattern()).split('/').join('')
      let vehicleIds =
        rootGetters['search_module/getSelection'].selectedVehiclesIds
      await api.v3.get(
        `${api.APIUrls.APIV3_GEORED_REALTIME_SERVICE_TO_KML}/${locale}`,
        {
          vehicleId: vehicleIds,
        },
        {
          isDownload: true,
          defaultDownloadFilename: `realtime_vehicle_${datePart}.kml`,
          forceDefaultDownloaFilename: false,
          headers: {
            accept: 'application/xml',
          },
          responseType: 'blob',
        }
      )
      showToast('export_feature.file_export_success', true)
    } catch (err) {
      showToast('export_feature.file_export_fail', true)
      console.error('Fail to export kml', {
        err,
      })
    }
  },
  async exportHistoryVehicleCSV({ rootGetters }) {
    try {
      //Show toast during execution
      showToast('export_feature.file_loading')

      let selectedItem = rootGetters['location_module/selectedItem']

      // Get selected vehicle
      let selectedVehicle = selectedItem.vehicleId

      if (!selectedVehicle) {
        console.error(
          'Error during csv vehicle history export : no vehicle selected'
        )
        return
      }

      // Get start and end datetime from date
      let date = selectedItem.date

      if (!date) {
        console.error(
          'Error during csv vehicle history export : no date selected'
        )
        return
      }

      let startDateTime = moment(date)
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss')

      let endDateTime = moment(date).endOf('day').format('YYYY-MM-DDTHH:mm:ss')

      if (!startDateTime || !endDateTime) {
        console.error(
          'Error during csv vehicle history export : both start and end datetime should be selected'
        )
        return
      }

      let response = await api.v3.get(
        `${api.APIUrls.APIV3_GEORED_SERVICE_HISTORY_TO_CSV}`,
        {
          vehicleId: selectedVehicle,
          startDatetime: startDateTime,
          endDatetime: endDateTime,
        },
        {
          headers: {
            accept: 'text/csv',
          },
          responseType: 'blob',
        }
      )

      // Get csv content
      let csvText = await response.data.text()

      //Convert CSV to JSON
      let parsedCsv = await csvToJson(csvText)

      //Get columns translations dynamically
      let columnNames = getTranslationsFromArrayOfString(
        Object.keys(parsedCsv[0])
      )

      //Get file prefix and date
      //Format filename as in Geored V2 (#47616)
      const filenamePrefix = i18n.t(
        'export_feature.history_vehicle.file_prefix'
      )
      const filenameSuffix = moment(date).format('YYYY-MM-DD')

      //Export CSV
      exportToCSV(parsedCsv, `${filenamePrefix}_${filenameSuffix}`, {
        columns: columnNames,
      })

      //Remove toast after file download
      await store.dispatch('alert/removeAlert')
    } catch (err) {
      showToast('export_feature.file_export_fail', true)
      console.error('Fail to export csv', {
        err,
      })
    }
  },
}
